import { type ComponentType, lazy } from "react";

const CHUNK_RELOAD_AT_KEY = "ai-deck-chunk-reload-at";

/** この間隔以内の連続失敗は自動リロードせず ErrorBoundary に委ねる（リロードループ防止） */
const RELOAD_MIN_INTERVAL_MS = 30_000;

/**
 * lazy chunk のロード失敗時に 1 回だけページを自動リロードして回復を試みる import ラッパー。
 *
 * デプロイ直後は「手元の古い index.html が、新デプロイで消えた旧ハッシュの chunk を要求 →
 * 404 で SPA フォールバックの HTML が返り MIME エラー」で dynamic import が失敗し、
 * ErrorBoundary の全面エラー画面になる（2026-07-10 に本番で実測）。
 * リロードすれば新しい index.html と chunk 一式が揃うため、利用者に見せずに自己回復できる。
 * 直近のリロードから RELOAD_MIN_INTERVAL_MS 以内の再失敗は本物の障害とみなして throw する。
 */
export const importWithChunkReload = async <T>(importer: () => Promise<T>): Promise<T> => {
  try {
    return await importer();
  } catch (error) {
    let canReload = false;
    try {
      const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_AT_KEY) ?? 0);
      if (Date.now() - lastReloadAt > RELOAD_MIN_INTERVAL_MS) {
        sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(Date.now()));
        canReload = true;
      }
    } catch {
      // sessionStorage 不可な環境ではリロードループを防げないため自動リロードしない
    }
    if (!canReload) {
      throw error;
    }
    window.location.reload();
    // リロード完了までは Suspense の fallback を表示し続ける（エラー画面を出さない）
    return new Promise<T>(() => {});
  }
};

// biome-ignore lint/suspicious/noExplicitAny: React.lazy と同じく任意の props を持つコンポーネントを受ける
export const lazyWithReload = <T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
) => lazy(() => importWithChunkReload(importer));
